import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, Check, X, MessageSquareCode } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { itemVariants } from "./motion";
import type { QuickReply } from "../mockAutomations";

interface QuickRepliesCardProps {
  replies: QuickReply[];
  onAdd: (reply: Omit<QuickReply, "id">) => void;
  onUpdate: (id: string, patch: Partial<QuickReply>) => void;
  onRemove: (id: string) => void;
}

export function QuickRepliesCard({ replies, onAdd, onUpdate, onRemove }: QuickRepliesCardProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ shortcut: "", message: "" });
  const [adding, setAdding] = useState(false);
  const [newReply, setNewReply] = useState({ shortcut: "", message: "" });

  const startEdit = (reply: QuickReply) => {
    setEditingId(reply.id);
    setDraft({ shortcut: reply.shortcut, message: reply.message });
  };

  const saveEdit = () => {
    if (editingId && draft.shortcut.trim() && draft.message.trim()) {
      onUpdate(editingId, draft);
    }
    setEditingId(null);
  };

  const saveNew = () => {
    if (newReply.shortcut.trim() && newReply.message.trim()) {
      onAdd(newReply);
      setNewReply({ shortcut: "", message: "" });
      setAdding(false);
    }
  };

  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <MessageSquareCode className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Quick Replies
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
              {replies.length} Snippets
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => setAdding(true)}
            className="h-7.5 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold shadow-xs gap-1 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add Snippet
          </Button>
        </div>

        <div className="p-3 bg-white/70 dark:bg-slate-900/70 space-y-2 flex-1">
          <AnimatePresence initial={false}>
            {adding && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2 rounded-xl border border-dashed border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-950/30 p-2.5 mb-2">
                  <Input
                    placeholder="/shortcut (e.g. /greeting)"
                    value={newReply.shortcut}
                    onChange={(e) => setNewReply((p) => ({ ...p, shortcut: e.target.value }))}
                    className="h-8.5 text-xs bg-white dark:bg-slate-900 rounded-xl"
                  />
                  <textarea
                    placeholder="Reply message text..."
                    rows={2}
                    value={newReply.message}
                    onChange={(e) => setNewReply((p) => ({ ...p, message: e.target.value }))}
                    className="w-full resize-y rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  />
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setAdding(false)} className="h-7.5 px-3 rounded-full text-xs">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveNew} className="h-7.5 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
                      Save
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {replies.length === 0 && !adding && (
            <p className="py-4 text-center text-xs text-slate-400 dark:text-slate-500">No quick replies configured yet.</p>
          )}

          <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-0.5">
            {replies.map((reply) => (
              <div key={reply.id} className="rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
                {editingId === reply.id ? (
                  <div className="space-y-2">
                    <Input
                      value={draft.shortcut}
                      onChange={(e) => setDraft((p) => ({ ...p, shortcut: e.target.value }))}
                      className="h-8 text-xs bg-white dark:bg-slate-900"
                    />
                    <textarea
                      rows={2}
                      value={draft.message}
                      onChange={(e) => setDraft((p) => ({ ...p, message: e.target.value }))}
                      className="w-full resize-y rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs text-slate-800 dark:text-slate-100"
                    />
                    <div className="flex justify-end gap-1.5">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" className="h-7 w-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <code className="text-xs font-bold text-emerald-700 dark:text-emerald-400 font-mono">{reply.shortcut}</code>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{reply.message}</p>
                    </div>
                    <div className="flex shrink-0 gap-0.5">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" onClick={() => startEdit(reply)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => onRemove(reply.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
